import React from "react";
import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";

export type Option = { label: string; value: string; disable?: boolean };

interface SelectFilterProps {
  label: string;
  placeholder: string;
  options: Option[];
  value: Option[];
  onChange: (value: Option[]) => void;
}

const getDisplayValue = (value: Option[], placeholder: string): string => {
  if (value.length === 0) return placeholder;
  if (value.length === 1) return value[0].label;
  return `${value.length} selected`;
};

export const SelectFilter: React.FC<SelectFilterProps> = ({
  label,
  placeholder,
  options,
  value,
  onChange,
}) => {
  const selectedValues = value.map((v) => v.value);

  const handleChange = (event: SelectChangeEvent<string[]>): void => {
    const selected = event.target.value as string[];
    const newValue = options.filter((opt) => selected.includes(opt.value));
    onChange(newValue);
  };

  const hasValue = value.length > 0;

  return (
    <FormControl fullWidth sx={{ flex: 1 }} size="small">
      {hasValue && (
        <InputLabel id={`${label}-label`} shrink>
          {label}
        </InputLabel>
      )}
      <Select<string[]>
        MenuProps={{
          disablePortal: false,
          disableScrollLock: true,
        }}
        labelId={`${label}-label`}
        id={`${label}-select`}
        multiple
        value={selectedValues}
        label={hasValue ? label : undefined}
        onChange={handleChange}
        displayEmpty
        notched={hasValue}
        sx={{
          ...(hasValue && {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "brandSupporting.main",
              borderWidth: 2,
            },
          }),
        }}
        renderValue={() =>
          hasValue ? (
            <Box component="span" color="text.primary">
              {getDisplayValue(value, placeholder)}
            </Box>
          ) : (
            <Box component="span" color="text.disabled">
              {placeholder}
            </Box>
          )
        }
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value} disabled={false}>
            <Checkbox
              checked={selectedValues.includes(option.value)}
              disabled={false}
            />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
